/**
 *  Интерполяционный многочлен Лагранжа
 *  @see {@link https://ru.wikibooks.org/wiki/%D0%A0%D0%B5%D0%B0%D0%BB%D0%B8%D0%B7%D0%B0%D1%86%D0%B8%D0%B8_%D0%B0%D0%BB%D0%B3%D0%BE%D1%80%D0%B8%D1%82%D0%BC%D0%BE%D0%B2/%D0%98%D0%BD%D1%82%D0%B5%D1%80%D0%BF%D0%BE%D0%BB%D1%8F%D1%86%D0%B8%D1%8F/%D0%9C%D0%BD%D0%BE%D0%B3%D0%BE%D1%87%D0%BB%D0%B5%D0%BD_%D0%9B%D0%B0%D0%B3%D1%80%D0%B0%D0%BD%D0%B6%D0%B0 WikiBooks}
 */
const interpolateLagrangePolynomial = (xValues, yValues) => x => {
    const size = Math.min(xValues.length, yValues.length);
    let lagrangePol = 0;

    for (let i = 0; i < size; i++) {
        let basicsPol = 1;

        for (let j = 0; j < size; j++) {
            if (j != i) {
                basicsPol *= (x - xValues[j]) / (xValues[i] - xValues[j]);
            }
        }

        lagrangePol += basicsPol * yValues[i];
    }

    return lagrangePol;
};

/*  Алгоритм многочлена Лагранжа обладает высокой точностью, но достаточно медленный на больших данных
    Для оптимизации его работы необходимо дробить исходные массив на чанки

    Возвращает Record<относительная грубина, абсолютная грубина>
*/
function interpolateLagrange(xValues, yValues) {
    const result = {};

    for (let i = 0; i < xValues.length; i += 2) {
        const xValuesChunk = xValues.slice(i, i + 3);
        const yValuesChunk = yValues.slice(i, i + 3);

        const min = Math.min(...xValuesChunk);
        const max = Math.max(...xValuesChunk);

        const interpolate = interpolateLagrangePolynomial(xValuesChunk, yValuesChunk);

        for (let j = min; j <= max; j++) {
            result[j] = interpolate(j);
        }
    }

    return result;
}

class NormalizeInclinometry {
    formatBase(data) {
        const length = data.length;
        return data.reduce(
            (acc, item, idx) => {
                const { inclinometryId, date, dStart, dStop, data, ...other } = item;

                /*
                    При формировании версии, объект data может быть сформирован с ошибкой.
                    Об этом будет свидетельствовать наличие поля error внутри объекта data.

                    При такой ситуации сейчас выкидывается исключение.

                    ! Важно: обрабатывать исключение.
                    TODO: Эта обработка должна находиться в модели. Сейчас за запрос отвечает hook.
                */
                if (data.error) {
                    console.error('Error in normalization of inclinometry.');
                    throw 'Error in normalization of inclinometry';
                }

                const singleVersion = {
                    inclinometryId,
                    date: this._formatDate(date),
                    depth: this._formatDepth(dStart, dStop),
                    ...other,
                };

                return {
                    versions: [...acc.versions, singleVersion],
                    details: { ...acc.details, [inclinometryId]: data },
                    lastVersionID: idx === length - 1 ? inclinometryId : null,
                };
            },
            { versions: [], details: {}, lastVersionID: null }
        );
    }

    toAbsDepth(data) {
        // TODO: пока что берем только последнюю инклинометрию и преобразует ее только в интерполяют абсолютный величин
        const item = data[data.length - 1];

        const { data: itemData } = item;

        /*
            При формировании версии, объект data может быть сформирован с ошибкой.
            Об этом будет свидетельствовать наличие поля error внутри объекта data.

            При такой ситуации сейчас выкидывается исключение.

            ! Важно: обрабатывать исключение.
            TODO: Эта обработка должна находиться в модели. Сейчас за запрос отвечает hook.
        */
        if (itemData.error) {
            console.error('Error in normalization of inclinometry.');
            throw 'Error in normalization of inclinometry';
        }

        const md = itemData.md ?? [];
        const z = itemData.z ?? [];

        const result = interpolateLagrange(md, z);

        return { rel: md, abs: z, data: result };
    }

    toLeftTable(data) {
        const { dx, dy, h_angle, md, v_angle, z } = data;

        return md.reduce((acc, it, i) => {
            const calcMaxDV = v_angle[i] - (v_angle[i - 1] || 0);
            return [
                ...acc,
                {
                    idx: i,
                    md: it,
                    vAngle: this._toFixed(v_angle[i]),
                    hAngle: this._toFixed(h_angle[i]),
                    maxDV: this._toFixed(calcMaxDV),
                    dx: dx[i],
                    dy: dy[i],
                    z: z[i],
                },
            ];
        }, []);
    }

    toTable(inclinometryData) {
        const { OutfallX, OutfallY, data } = inclinometryData;
        const { dx, dy, h_angle, md, v_angle, z } = data;

        const isHValidValue = angle => {
            const kNullValue = -999.25;
            const kEpsilon = 0.0001;
            return Math.abs(angle - kNullValue) > kEpsilon;
        };

        const z0 = data.z[0];
        return md.reduce((acc, it, i) => {
            const calcMaxDV = v_angle[i] - (v_angle[i - 1] || 0);

            const cucl_i = () => {
                if (isHValidValue(h_angle[i]) && isHValidValue(h_angle[i - 1])) {
                    if (md[i] - md[i - 1]) {
                        return 1;
                    } else {
                        return 0;
                    }
                } else {
                    return '-';
                }
            };

            return [
                ...acc,
                {
                    idx: i,
                    md: it, //DONE (Глубина)
                    v: this._toFixed(v_angle[i]), //DONE
                    h: this._toFixed(h_angle[i]), //DONE
                    i: cucl_i(), // WIP
                    dv: this._toFixed(calcMaxDV), //DONE
                    dx: dx[i].toFixed(2), // DONE
                    dy: dy[i].toFixed(2), //DONE
                    dz: (z[i] - z0).toFixed(2), // Check
                    lengthening: (it - z[i] + z0).toFixed(2), //Math.sqrt(dx[i] * dx[i] + dy[i] * dy[i] + (z[i] - z0) * (z[i] - z0)), //WIP
                },
            ];
        }, []);
    }

    toChart(inclinometryData) {
        const { OutfallX, OutfallY, data } = inclinometryData;
        const { dx, dy, h_angle, md, v_angle, z } = data;

        return md.reduce((acc, it, i) => {
            // const calcMaxDV = v_angle[i] - (v_angle[i - 1] || 0);
            return [
                ...acc,
                {
                    // idx: i,
                    // md: it,
                    // vAngle: this._toFixed(v_angle[i]),
                    // hAngle: this._toFixed(h_angle[i]),
                    // maxDV: this._toFixed(calcMaxDV),
                    x: dx[i] + OutfallX,
                    y: dy[i] + OutfallY,
                    z: z[i],
                },
            ];
        }, []);
    }

    toChart_v2(inclinometryData) {
        const { OutfallX, OutfallY, data } = inclinometryData;
        const { dx, dy, h_angle, md, v_angle, z } = data;

        const x = dx.map(i => i + OutfallX);
        const y = dy.map(i => i + OutfallY);

        return {
            x: x,
            y: y,
            z: z,
        };
    }

    _toFixed(num, round = 2) {
        return num.toFixed(round);
    }

    _formatDate(date) {
        return new Date(date).toLocaleDateString('ru-RU');
    }

    _formatDepth(dStart, dStop) {
        return `${this._toFixed(dStart)}-${this._toFixed(dStop)}`;
    }
}

export const normalize = new NormalizeInclinometry();
